import type { BaseSyntheticEvent } from 'react';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type {
  FieldValue,
  FormField,
  FormStatus,
  FormThemeValue
} from '@elfsight/widget-form-builder/widget';
import {
  FieldComponent as Field,
  SubmitButton
} from '@elfsight/widget-form-builder/widget';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import { Loader } from '@elfsight/widget-ui';
import type { ContentDirection } from '../../../types';
import type { BuilderContentAlignment } from '../../Builder';

const FULL_WIDTH = 100;

const LABEL_ONLY_OUTSIDE_FIELDS = ['choice', 'dropdown'];

type RowField = FormField & { columns: [number, number] };

const FORM_MAX_WIDTH_PX = 420;

interface VerticalLayoutProps {
  theme: FormThemeValue;
  formSettings: any;
  alignment?: BuilderContentAlignment;
  submitButtonAlignment?: 'left' | 'center' | 'right';
  status?: FormStatus;
  handleSubmit(e: BaseSyntheticEvent): Promise<void>;
  handleChange?(value?: FieldValue): void;
  contentDirection?: ContentDirection;
}

export const VerticalLayout = ({
  theme,
  formSettings,
  handleSubmit,
  handleChange,
  alignment = 'center',
  submitButtonAlignment = 'center',
  contentDirection = 'column',
  status
}: VerticalLayoutProps) => {
  const { accentColor, errorColor } = theme;
  const { fieldLabelPosition, fields, submitButtonProps } = formSettings;

  const fieldRef = useRef<TextControlBaseInstance | null>(null);
  const prevFieldsLength = useRef<number>(fields.length);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const rows = useMemo(() => {
    let width = 0;
    let currentWidth = 0;
    let row: RowField[] = [];
    const rows = [];

    fields.forEach((field: FormField) => {
      currentWidth = width + field.width;
      if (currentWidth > FULL_WIDTH) {
        rows.push(row);
        width = 0;
        currentWidth = field.width;
        row = [];
      }
      row.push({ ...field, columns: [width + 1, currentWidth + 1] });
      width = currentWidth;

      if (
        field.id === focusedField &&
        prevFieldsLength.current !== fields.length
      ) {
        setTimeout(() => {
          fieldRef.current?.focusInput?.();
        }, 100);
      }
    });

    if (row.length) rows.push(row);

    prevFieldsLength.current = fields.length;

    return rows;
  }, [fields]);

  return (
    <>
      <FormContainer
        onSubmit={(e) => e.preventDefault()}
        _alignSelf={alignment}
      >
        {rows.map((row, i) => (
          <Row key={i}>
            {row.map((field: RowField) => {
              const rowHasLabelInside =
                fieldLabelPosition === 'inside' &&
                row.some(
                  ({ showLabel, label, type }) =>
                    showLabel &&
                    !!label &&
                    !LABEL_ONLY_OUTSIDE_FIELDS.includes(type)
                );

              return (
                <Field
                  ref={field.id === focusedField ? fieldRef : undefined}
                  key={field.id}
                  field={field}
                  fieldPadding={0}
                  columns={field.columns}
                  formSettings={formSettings}
                  contentDirection={contentDirection}
                  rowHasLabelInside={rowHasLabelInside}
                  errorColor={errorColor}
                  onFocus={() => setFocusedField(field.id)}
                  onChange={handleChange}
                  addBottomMarginToLastChild
                />
              );
            })}
          </Row>
        ))}
      </FormContainer>
      {status === 'pending' ? (
        <Loader color={accentColor} />
      ) : (
        <ButtonContainer
          _alignment={submitButtonAlignment}
          _alignSelf={alignment}
        >
          <SubmitButton {...submitButtonProps} onClick={handleSubmit} />
        </ButtonContainer>
      )}
    </>
  );
};

const FormContainer = styled.form<{ _alignSelf?: string }>`
  width: 100%;
  max-width: ${FORM_MAX_WIDTH_PX}px;
  align-self: ${({ _alignSelf }) => _alignSelf};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(${FULL_WIDTH}, 1fr);
  grid-template-rows: repeat(3, max-content);
`;

const ButtonContainer = styled.div<{
  _alignment?: string;
  _alignSelf?: string;
}>`
  display: flex;
  width: 100%;
  max-width: ${FORM_MAX_WIDTH_PX}px;
  justify-content: ${({ _alignment }) => _alignment};
  align-self: ${({ _alignSelf }) => _alignSelf};
`;
